import React, { useEffect, useRef, useState } from "react";
import Head from "next/head";
import { NextSeo } from "next-seo";
import { wrapper } from "../src/store";
import { END } from "redux-saga";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { handleImageLink } from "@/helpers/functions";
import {
  getPageData,
  getSettings,
  getSectionData,
  getServices,
  getProjects,
  getProjectTypes,
  getSocials,
  getClients,
  getContactSection,
} from "@/store/actions";

const Header = dynamic(() => import("@/components/header/Index"));

const Footer = dynamic(() => import("@/components/footer/Index"), {
  ssr: false,
});

const HeroSection = dynamic(() => import("@/components/hero-section/Index"));

const AboutSection = dynamic(() => import("@/components/about-section/Index"), {
  ssr: false,
});

const ServicesSection = dynamic(
  () => import("@/components/services-section/Index"),
  {
    ssr: false,
  },
);

const HowWeWorkSection = dynamic(
  () => import("@/components/how-we-work-section/Index"),
  {
    ssr: false,
  },
);

const WorksSection = dynamic(() => import("@/components/works-section/Index"), {
  ssr: false,
});

const SubscribeSection = dynamic(
  () => import("@/components/subscribe-section/Index"),
  {
    ssr: false,
  },
);

const ClientsSection = dynamic(
  () => import("@/components/clients-section/Index"),
  {
    ssr: false,
  },
);

const VrSection = dynamic(() => import("@/components/vr/Index"), {
  ssr: false,
});

const ProfileDownloadSection = dynamic(
  () => import("@/components/profile-download/Index"),
  {
    ssr: false,
  },
);

const LazyMount = ({ enabled, children, minHeight = 0 }) => {
  const holderRef = useRef(null);
  const [isMounted, setIsMounted] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setIsMounted(true);
      return;
    }

    if (typeof window === "undefined") return;

    const node = holderRef.current;
    if (!node) return;

    if (!("IntersectionObserver" in window)) {
      setIsMounted(true);
      return;
    }

    let active = true;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          if (active) setIsMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" },
    );

    observer.observe(node);

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [enabled]);

  return (
    <div ref={holderRef} style={!isMounted && minHeight ? { minHeight } : {}}>
      {isMounted ? children : null}
    </div>
  );
};

const businessSchemaByLocale = {
  ar: {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://designconcepts.sa/#business-ar",
    name: "مفاهيم التصميم",
    alternateName: "Design Concepts",
    url: "https://designconcepts.sa/",
    logo: "https://desginconcept.ejjadh.info/uploads/file-1767958980593.png",
    image: "https://desginconcept.ejjadh.info/uploads/file-1767958980593.png",
    telephone: "+966541302695",
    email: "info@designconcepts.sa",
    address: {
      "@type": "PostalAddress",
      addressLocality: "الرياض",
      addressCountry: "SA",
    },
    areaServed: {
      "@type": "Country",
      name: "Saudi Arabia",
    },
    sameAs: [
      "https://www.facebook.com/designconcepts001",
      "https://x.com/d_c_sa",
      "https://www.instagram.com/d_c_sa/",
      "https://www.snapchat.com/@designcon2016",
      "https://www.tiktok.com/@d_c_sa",
      "https://maps.app.goo.gl/vSVLBkVYeBKecNEQA",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "الخدمات",
      itemListElement: [
        {
          "@type": "Service",
          name: "لوحة جمع الافكار",
          url: "https://designconcepts.sa/services/Idea-collection-board",
        },
        {
          "@type": "Service",
          name: "الإشراف الهندسي على المواقع",
          url: "https://designconcepts.sa/services/Engineering-supervision-of-sites",
        },
        {
          "@type": "Service",
          name: "مخططات تنفيذية تفصيلية",
          url: "https://designconcepts.sa/services/Detailed-implementation-plans",
        },
        {
          "@type": "Service",
          name: "مناظير خارجية وداخلية ثلاثية الأبعاد",
          url: "https://designconcepts.sa/services/3D-external-and-internal-viewfinders",
        },
        {
          "@type": "Service",
          name: "أعمال مخططات الكهرباء والسباكة",
          url: "https://designconcepts.sa/services/Electrical-and-Plumbing-Drawings",
        },
        {
          "@type": "Service",
          name: "الرفع المساحي للموقع",
          url: "https://designconcepts.sa/services/Site-surveying",
        },
        {
          "@type": "Service",
          name: "تنسيق وتوريد الأثاث",
          url: "https://designconcepts.sa/services/Furniture-Coordination-and-Supply",
        },
      ],
    },
  },
  en: {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": "https://designconcepts.sa/#business-en",
    name: "Design Concepts",
    alternateName: "مفاهيم التصميم",
    url: "https://designconcepts.sa/en",
    logo: "https://desginconcept.ejjadh.info/uploads/file-1767958980593.png",
    image: "https://desginconcept.ejjadh.info/uploads/file-1767958980593.png",
    telephone: "+966541302695",
    email: "info@designconcepts.sa",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Riyadh",
      addressCountry: "SA",
    },
    areaServed: {
      "@type": "Country",
      name: "Saudi Arabia",
    },
    sameAs: [
      "https://www.facebook.com/designconcepts001",
      "https://x.com/d_c_sa",
      "https://www.instagram.com/d_c_sa/",
      "https://www.snapchat.com/@designcon2016",
      "https://www.tiktok.com/@d_c_sa",
      "https://maps.app.goo.gl/vSVLBkVYeBKecNEQA",
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Services",
      itemListElement: [
        {
          "@type": "Service",
          name: "Idea collection board",
          url: "https://designconcepts.sa/en/services/Idea-collection-board",
        },
        {
          "@type": "Service",
          name: "Engineering Site Supervision",
          url: "https://designconcepts.sa/en/services/Engineering-supervision-of-sites",
        },
        {
          "@type": "Service",
          name: "Detailed implementation plans",
          url: "https://designconcepts.sa/en/services/Detailed-implementation-plans",
        },
        {
          "@type": "Service",
          name: "3D Exterior and Interior Renderings",
          url: "https://designconcepts.sa/en/services/3D-external-and-internal-viewfinders",
        },
        {
          "@type": "Service",
          name: "Electrical and Plumbing Drawings",
          url: "https://designconcepts.sa/en/services/Electrical-and-Plumbing-Drawings",
        },
        {
          "@type": "Service",
          name: "Site Surveying",
          url: "https://designconcepts.sa/en/services/Site-surveying",
        },
        {
          "@type": "Service",
          name: "Furniture Coordination & Supply",
          url: "https://designconcepts.sa/en/services/Furniture-Coordination-and-Supply",
        },
      ],
    },
  },
};

const faqSchemaByLocale = {
  ar: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: "ar",
    mainEntity: [
      {
        "@type": "Question",
        name: "ماذا سأستلم عند التصميم؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "ملف تصميم ويشمل رسومات الثري دي والمخططات التفصيلية بالإضافة إلى جداول كميات مواد التشطيب",
        },
      },
      {
        "@type": "Question",
        name: "هل يتم توفير الأثاث؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "نعم يتم توفير وتوريد الأثاث جاهز وتفصيل حسب المعتمد في التصميم",
        },
      },
      {
        "@type": "Question",
        name: "كم مدة العمل على التصميم؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "مدة العمل على التصميم تعتمد على تفاصيل التصميم المطلوبة بناء على ما يناسب الموقع",
        },
      },
      {
        "@type": "Question",
        name: "كم مدة العمل على التنفيذ؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "مدة أعمال التنفيذ تعتمد بشكل كبير على أعمال التصميم، بعد اعتماد التصميم الداخلي يتم تحديد مدة تنفيذ مشروعك.",
        },
      },
      {
        "@type": "Question",
        name: "كم سعر التنفيذ؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "يتم تحديد تكلفة التنفيذ بعد اعتماد التصاميم، مع العلم أن تسعيرة التصميم منفصلة تمامًا عن تسعيرة التنفيذ.",
        },
      },
    ],
  },
  en: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: "en",
    mainEntity: [
      {
        "@type": "Question",
        name: "What will I receive upon design?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "A design file that includes 3D drawings, detailed plans, and bills of quantities for finishing materials.",
        },
      },
      {
        "@type": "Question",
        name: "Is furniture provided?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes, furniture is supplied ready-made and custom-made according to the approved design.",
        },
      },
      {
        "@type": "Question",
        name: "How long does it take to work on the design?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The design work time depends on the required design details and what suits the site.",
        },
      },
      {
        "@type": "Question",
        name: "How long will the implementation process take?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The duration of the execution works depends largely on the design works. After the interior design is approved, the duration of your project is determined.",
        },
      },
      {
        "@type": "Question",
        name: "What is the cost of execution?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "The cost of implementation is determined after the designs are approved, noting that the design pricing is completely separate from the implementation pricing.",
        },
      },
    ],
  },
};

const Home = () => {
  const { locale } = useRouter();
  const { sectionData, settings } = useSelector((state) => state.settings);

  const slidesWrapperRef = useRef(null);
  const [canInitScrollEffects, setCanInitScrollEffects] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const updateMobileState = () => setIsMobile(mediaQuery.matches);

    updateMobileState();
    mediaQuery.addEventListener("change", updateMobileState);

    return () => {
      mediaQuery.removeEventListener("change", updateMobileState);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (!isDesktop || prefersReducedMotion) return;

    let enabled = false;
    const enableEffects = () => {
      if (enabled) return;
      enabled = true;
      setCanInitScrollEffects(true);
      window.removeEventListener("scroll", enableEffects);
      window.removeEventListener("wheel", enableEffects);
      window.removeEventListener("touchmove", enableEffects);
    };

    window.addEventListener("scroll", enableEffects, { passive: true });
    window.addEventListener("wheel", enableEffects, { passive: true });
    window.addEventListener("touchmove", enableEffects, { passive: true });

    return () => {
      window.removeEventListener("scroll", enableEffects);
      window.removeEventListener("wheel", enableEffects);
      window.removeEventListener("touchmove", enableEffects);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!slidesWrapperRef.current) return;
    if (!canInitScrollEffects) return;

    let cancelled = false;
    let cleanup = () => {};

    const initScrollAnimations = async () => {
      const [{ default: gsap }, { default: ScrollTrigger }] = await Promise.all(
        [import("gsap"), import("gsap/dist/ScrollTrigger")],
      );

      if (cancelled || !slidesWrapperRef.current) return;

      gsap.registerPlugin(ScrollTrigger);

      const panels = gsap.utils.toArray(
        slidesWrapperRef.current.querySelectorAll(".section"),
      );

      panels.pop();

      const windowHeight = window.innerHeight;
      const measuredPanels = panels
        .map((panel) => {
          const innerpanel = panel.querySelector(".section-inner");
          if (!innerpanel) return null;

          const panelHeight = innerpanel.offsetHeight;
          const difference = panelHeight - windowHeight;
          const fakeScrollRatio =
            difference > 0 ? difference / (difference + windowHeight) : 0;

          return {
            panel,
            innerpanel,
            panelHeight,
            fakeScrollRatio,
            endValue: fakeScrollRatio ? `+=${panelHeight}` : "bottom top",
          };
        })
        .filter(Boolean);

      measuredPanels.forEach(
        ({ panel, innerpanel, panelHeight, fakeScrollRatio, endValue }) => {
          const shouldApplyFakeScroll =
            fakeScrollRatio && !panel.classList.contains("section-4");

          if (shouldApplyFakeScroll) {
            panel.style.marginBottom = panelHeight * fakeScrollRatio + "px";
          }

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: panel,
              start: "bottom bottom",
              end: endValue,
              pinSpacing: false,
              pin: true,
              scrub: true,
            },
          });

          if (shouldApplyFakeScroll) {
            tl.to(innerpanel, {
              yPercent: -100,
              y: windowHeight,
              duration: 1 / (1 - fakeScrollRatio) - 1,
              ease: "none",
            });
          }

          tl.fromTo(
            panel,
            { scale: 1, opacity: 1 },
            { scale: 0.7, opacity: 0.5, duration: 0.9 },
          ).to(panel, { opacity: 0, duration: 0.1 });
        },
      );

      cleanup = () => {
        ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      };
    };

    if (window.requestIdleCallback) {
      window.requestIdleCallback(initScrollAnimations, { timeout: 1500 });
    } else {
      setTimeout(initScrollAnimations, 300);
    }

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [canInitScrollEffects]);

  const businessSchema =
    locale === "en" ? businessSchemaByLocale.en : businessSchemaByLocale.ar;
  const faqSchema =
    locale === "en" ? faqSchemaByLocale.en : faqSchemaByLocale.ar;

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(businessSchema),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      </Head>

      <NextSeo
        title={`${sectionData?.metaTitle?.[locale]} | ${settings?.appName?.[locale]}`}
        description={sectionData?.metaDescription?.[locale]}
        openGraph={{
          title: sectionData?.metaTitle?.[locale],
          description: sectionData?.metaDescription?.[locale],
        }}
        // canonical={
        //   typeof window !== "undefined"
        //     ? window.location.href
        //     : `${process.env.NEXT_PUBLIC_SITE_URL}/${asPath}`
        // }
        twitter={{
          cardType: "summary_large_image",
          handle: "@handle",
          site: "@site",
          title: sectionData?.metaTitle?.[locale],
          description: sectionData?.metaDescription?.[locale],
          image: handleImageLink(settings?.appLogo),
        }}
      />
      <Header />

      {/* هنا بنطبق نفس ستركتشر الـ CodePen */}
      <main className="slides-wrapper" ref={slidesWrapperRef}>
        <section className="section section-1">
          <div className="section-content">
            <div className="section-inner">
              <HeroSection />
            </div>
          </div>
        </section>

        <section className="section section-2">
          <div className="section-content">
            <div className="section-inner">
              <AboutSection />
            </div>
          </div>
        </section>

        <section className="section section-3">
          <div className="section-content">
            <div className="section-inner">
              <LazyMount enabled={isMobile} minHeight={500}>
                <ServicesSection />
              </LazyMount>
            </div>
          </div>
        </section>

        <section className="section section-4">
          <div className="section-content">
            <div className="section-inner">
              <LazyMount enabled={isMobile} minHeight={420}>
                <HowWeWorkSection />
              </LazyMount>
            </div>
          </div>
        </section>

        <section className="section section-5">
          <div className="section-content">
            <div className="section-inner">
              <LazyMount enabled={isMobile} minHeight={520}>
                <WorksSection />
              </LazyMount>
            </div>
          </div>
        </section>

        <section className="section section-6">
          <div className="section-content">
            <div className="section-inner">
              <LazyMount enabled={isMobile} minHeight={420}>
                <VrSection />
              </LazyMount>
            </div>
          </div>
        </section>

        <section
          className="section section-7"
          style={{
            backgroundColor: "#e4d5be54",
          }}
        >
          <div className="section-content">
            <div className="section-inner">
              <LazyMount enabled={isMobile} minHeight={420}>
                <ProfileDownloadSection />
              </LazyMount>
            </div>
          </div>
        </section>

        <section className="section section-8">
          <div className="section-content">
            <div className="section-inner">
              <LazyMount enabled={isMobile} minHeight={700}>
                <>
                  <SubscribeSection />
                  <ClientsSection />
                  <Footer />
                </>
              </LazyMount>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export const getStaticProps = wrapper.getStaticProps((store) => {
  return async () => {
    store.dispatch(getPageData({ cookies: {}, slug: "home" }));
    store.dispatch(
      getSectionData({ cookies: {}, pageSlug: "seo", sectionSlug: "homepage" }),
    );
    store.dispatch(
      getSettings({
        cookies: {},
      }),
    );
    store.dispatch(getServices({ cookies: {} }));
    store.dispatch(getProjects({ cookies: {} }));
    store.dispatch(getProjectTypes({ cookies: {} }));
    store.dispatch(getSocials({ cookies: {} }));
    store.dispatch(getClients({ cookies: {} }));
    store.dispatch(getContactSection({ cookies: {} }));

    store.dispatch(END);
    await store.sagaTask.toPromise();

    // const state = store.getState();
    // const pageData = state?.settings?.pageData;
    // const sectionData = state?.settings?.sectionData;

    return {
      props: {},
      revalidate: 1,
    };
  };
});

export default Home;
